import { type SEOHandle } from '@nasa-gcn/remix-seo'
import React, { useState } from 'react'
import { NavLink, Outlet, useOutletContext } from 'react-router'
import { Footer } from '#app/components/footer.tsx'
import { Header } from '#app/components/header.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/lib/utils'
import { type Theme } from '#app/utils/theme.server.ts'

function cnx({ isActive }: { isActive: boolean }): string {
	return cn(
		'line-clamp-2 block rounded-md px-4 py-3 text-lg md:ml-2 md:rounded-l-full md:text-xl',
		isActive
			? 'bg-primary text-primary-foreground'
			: 'text-secondary-foreground hover:bg-secondary',
	)
}

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export default function RootLayout() {
	const { theme } = useOutletContext<{ theme: Theme }>()
	const [isMenuOpen, setIsMenuOpen] = useState(false)

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen)
	}

	return (
		<div className="min-h-screen p-2 md:p-4 lg:p-8">
			<Header className="pb-2 md:pb-4 lg:pb-8" userPreference={theme} />
			<div className="flex w-full flex-col bg-primary-foreground pb-2 md:flex-row md:pb-4 lg:container lg:mx-auto lg:pb-8">
				<div className="mb-2 flex w-full flex-col p-4 md:w-1/4 md:p-2 lg:w-1/5">
					<div className="mb-4 flex items-center justify-between md:hidden">
						<h1 className="text-2xl font-bold">Menu</h1>
						<button
							onClick={toggleMenu}
							className="text-gray-800 focus:outline-hidden"
							aria-label="Toggle navigation menu"
						>
							{isMenuOpen ? (
								<Icon name="cross-1" className="h-8 w-8" />
							) : (
								<Icon name="hamburger-menu" className="h-8 w-8" />
							)}
						</button>
					</div>

					<nav
						className={cn(
							'flex-col md:flex',
							isMenuOpen ? 'flex' : 'hidden md:flex',
						)}
					>
						<h1 className="mb-4 text-center text-2xl font-bold md:text-left">
							Legal Notices
						</h1>
						<ul>
							<li>
								<NavLink
									to="/legal-notice/acceptable-use"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									Acceptable Use Policy
								</NavLink>
							</li>
							<li>
								<NavLink
									to="/legal-notice/community-guidelines"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									Community Guidelines
								</NavLink>
							</li>
							<li>
								<NavLink
									to="/legal-notice/cookie"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									Cookie Policy
								</NavLink>
							</li>
							<li>
								<NavLink
									to="/legal-notice/disclaimer"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									Disclaimer Policy
								</NavLink>
							</li>
							<li>
								<NavLink
									to="/legal-notice/eula"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									End User License Agreement
								</NavLink>
							</li>
							<li>
								<NavLink
									to="/legal-notice/privacy"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									Privacy Policy
								</NavLink>
							</li>
							<li>
								<NavLink
									to="/legal-notice/refund"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									Refund Policy
								</NavLink>
							</li>
							<li>
								<NavLink
									to="/legal-notice/terms-of-service"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									Terms of Service
								</NavLink>
							</li>
						</ul>

						<h1 className="mt-6 mb-4 text-center text-2xl font-bold md:text-left">
							Forms
						</h1>
						<ul>
							<li>
								<NavLink
									to="/legal-notice/data-subject-access-request"
									preventScrollReset
									prefetch="render"
									className={cnx}
								>
									Data Subject Access Request
								</NavLink>
							</li>
						</ul>
					</nav>
				</div>

				<div className="w-full p-2 md:w-3/4 lg:w-4/5">
					<Outlet />
				</div>
			</div>

			<hr />

			<Footer className="pt-2 md:pt-4 lg:pt-8" />
		</div>
	)
}
